import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePermission } from '../context/PermissionContext.tsx';
import {
  hasActionPermission,
  loadPermissionSnapshot,
  PermissionSnapshot,
  PERMISSION_SNAPSHOT_UPDATED_EVENT,
} from '../services/permissionGuard.ts';

interface CheckOptions {
  menu?: string;
  allowGlobalFallback?: boolean;
}

export function useActionPermission() {
  const location = useLocation();
  const permissionContext = usePermission();
  const [snapshotVersion, setSnapshotVersion] = useState(0);

  useEffect(() => {
    const handleSnapshotUpdated = () => setSnapshotVersion((v) => v + 1);
    const handleStorage = (e: StorageEvent) => {
      if (!e.key || e.key === 'permissionSnapshot') {
        setSnapshotVersion((v) => v + 1);
      }
    };

    window.addEventListener(PERMISSION_SNAPSHOT_UPDATED_EVENT, handleSnapshotUpdated);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(PERMISSION_SNAPSHOT_UPDATED_EVENT, handleSnapshotUpdated);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  // 依赖 snapshotVersion，确保 DynamicMenu 写入 localStorage 后同页立即生效
  void snapshotVersion;
  const fromStorage = loadPermissionSnapshot();
  const hasContextData =
    (permissionContext.actions && permissionContext.actions.length > 0) ||
    (permissionContext.scopedActions && permissionContext.scopedActions.length > 0);
  const hasStorageSnapshot = Boolean(fromStorage.timestamp);

  const snapshot: PermissionSnapshot = hasContextData
    ? {
        roleId: permissionContext.roleId,
        menuIds: permissionContext.menuIds,
        actions: permissionContext.actions,
        scopedActions: permissionContext.scopedActions,
      }
    : fromStorage;

  const isPermissionReady = hasContextData || hasStorageSnapshot;

  const can = (action: string, options?: CheckOptions) => {
    return hasActionPermission(snapshot, action, {
      ...options,
      routePath: location.pathname,
    });
  };

  const canAny = (actions: string[], options?: CheckOptions) => {
    return actions.some((code) => can(code, options));
  };

  const canAll = (actions: string[], options?: CheckOptions) => {
    return actions.every((code) => can(code, options));
  };

  return {
    can,
    canAny,
    canAll,
    actions: snapshot.actions,
    scopedActions: snapshot.scopedActions,
    isPermissionReady,
  };
}
